import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Minus, Plus } from "lucide-react";
import { services } from "@/data/services";

function contactHref(service) {
  return `/contact?service=${encodeURIComponent(service.contactLabel)}`;
}

function SectionIntro({ index, label, title, intro }) {
  return (
    <div className="max-w-3xl mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="font-mono text-sm text-slate-400">{String(index).padStart(2, "0")}</span>
        <span className="text-xs uppercase tracking-[0.18em] font-semibold text-blue-600 font-heading">{label}</span>
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">{title}</h2>
      {intro ? <p className="mt-4 text-slate-600 leading-relaxed">{intro}</p> : null}
    </div>
  );
}

function ItemList({ items }) {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
      {items.map((item) => (
        <li key={item} className="border-t border-slate-200 pt-3 text-slate-800">
          {item}
        </li>
      ))}
    </ul>
  );
}

function FaqList({ items }) {
  const [open, setOpen] = useState(0);

  return (
    <div className="border-t border-slate-200">
      {items.map((item, index) => {
        const isOpen = open === index;
        return (
          <div key={item.q} className="border-b border-slate-200">
            <button
              type="button"
              className="w-full flex items-start justify-between gap-6 py-5 text-left"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? -1 : index)}
            >
              <span className="font-semibold text-slate-900 font-heading">{item.q}</span>
              {isOpen ? <Minus className="w-4 h-4 mt-1 shrink-0 text-blue-600" /> : <Plus className="w-4 h-4 mt-1 shrink-0 text-slate-400" />}
            </button>
            {isOpen ? <p className="pb-5 max-w-3xl text-slate-600 leading-relaxed">{item.a}</p> : null}
          </div>
        );
      })}
    </div>
  );
}

function SectionBody({ section }) {
  if (section.type === "text-points") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-4">
          {section.paragraphs.map((paragraph) => (
            <p key={paragraph} className="text-slate-600 leading-relaxed">{paragraph}</p>
          ))}
        </div>
        <ul className="lg:col-span-5 border-t border-slate-200">
          {section.points.map((point) => (
            <li key={point} className="py-3 border-b border-slate-200 text-slate-800">{point}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (section.type === "items") {
    return <ItemList items={section.items} />;
  }

  if (section.type === "detail") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-4">
          {section.paragraphs.map((paragraph) => (
            <p key={paragraph} className="text-slate-600 leading-relaxed">{paragraph}</p>
          ))}
        </div>
        <div className="lg:col-span-7">
          <ItemList items={section.items} />
        </div>
      </div>
    );
  }

  if (section.type === "chips") {
    return (
      <ul className="flex flex-wrap gap-3">
        {section.items.map((item) => (
          <li key={item} className="px-4 py-2 border border-slate-200 text-sm text-slate-800 bg-white">
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (section.type === "process") {
    return (
      <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
        {section.steps.map((step, index) => (
          <li key={step.name} className="border-t border-slate-200 py-6">
            <p className="font-mono text-xs text-slate-400 mb-2">{String(index + 1).padStart(2, "0")}</p>
            <h3 className="text-lg font-semibold text-slate-900 font-heading">{step.name}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.text}</p>
          </li>
        ))}
      </ol>
    );
  }

  if (section.type === "compare") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 border border-slate-200">
        <div className="p-8 border-b md:border-b-0 md:border-r border-slate-200">
          <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-400 font-heading mb-4">{section.genericLabel}</p>
          <p className="text-2xl font-semibold text-slate-900 font-heading leading-snug">{section.generic}</p>
        </div>
        <div className="p-8 bg-slate-950 text-white">
          <p className="text-xs uppercase tracking-[0.18em] font-semibold text-cyan-300 font-heading mb-4">{section.customLabel}</p>
          <p className="text-2xl font-semibold font-heading leading-snug">{section.custom}</p>
        </div>
      </div>
    );
  }

  if (section.type === "flow") {
    return (
      <ol className="max-w-xl">
        {section.steps.map((step, index) => (
          <li key={step}>
            <div className="border border-slate-200 px-5 py-4 bg-white text-slate-900 font-medium">{step}</div>
            {index < section.steps.length - 1 ? <div className="h-6 w-px bg-slate-300 ml-6" aria-hidden="true" /> : null}
          </li>
        ))}
      </ol>
    );
  }

  if (section.type === "faq") {
    return <FaqList items={section.items} />;
  }

  return null;
}

export default function ServiceLanding({ service }) {
  const others = services.filter((item) => item.id !== service.id);

  useEffect(() => {
    document.title = `${service.title} | BizLogic`;
    return () => {
      document.title = "BizLogic";
    };
  }, [service.title]);

  return (
    <article data-testid="service-detail-page" className="pt-20">
      <header className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-14 lg:py-20">
          <Link
            to="/services"
            data-testid="service-detail-back"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            All services
          </Link>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 font-heading mb-4">{service.hero.eyebrow}</p>
          <h1 className="max-w-4xl text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 font-heading leading-tight">
            {service.hero.headline}
          </h1>
          <p className="max-w-3xl mt-6 text-lg text-slate-600 leading-relaxed">{service.hero.text}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to={contactHref(service)}
              data-testid={`service-panel-cta-${service.id}`}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3.5 rounded-xl transition-all shadow-md shadow-blue-600/10"
            >
              {service.hero.primaryLabel}
              <ArrowRight className="w-4 h-4" />
            </Link>
            {service.hero.secondaryLabel ? (
              <Link
                to={service.hero.secondaryTo}
                className="inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-800 font-medium px-6 py-3.5 rounded-xl hover:border-blue-600 hover:text-blue-600 transition-colors"
              >
                {service.hero.secondaryLabel}
              </Link>
            ) : null}
          </div>
        </div>
      </header>

      {service.sections.map((section, index) => {
        if (section.type === "cta") {
          return (
            <section key={section.title} className="bg-slate-950 text-white">
              <div className="max-w-6xl mx-auto px-6 sm:px-8 py-20 lg:py-24">
                <h2 className="max-w-3xl text-3xl sm:text-5xl font-bold tracking-tight font-heading">{section.title}</h2>
                <p className="max-w-2xl mt-4 text-slate-300 leading-relaxed">{section.text}</p>
                <Link
                  to={contactHref(service)}
                  className="inline-flex items-center gap-2 mt-8 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-xl"
                >
                  {section.label}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </section>
          );
        }

        return (
          <section key={section.title} className="border-b border-slate-200">
            <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 lg:py-24">
              <SectionIntro index={index + 1} label={section.label} title={section.title} intro={section.intro} />
              <SectionBody section={section} />
            </div>
          </section>
        );
      })}

      <section className="bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16">
          <h2 className="text-2xl font-bold text-slate-900 font-heading mb-8">Other services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {others.map((item) => (
              <Link
                key={item.id}
                to={`/services/${item.id}`}
                data-testid={`related-service-${item.id}`}
                className="bg-white p-6 hover:bg-slate-50 transition-colors"
              >
                <p className="text-xs uppercase tracking-[0.16em] text-blue-600 font-semibold font-heading mb-2">{item.badge}</p>
                <p className="font-semibold text-slate-900 font-heading">{item.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
